/**
 * Lightweight DotLottie bootstrap tailored for the Shopify theme.
 * Keeps canvas animations crisp and retains the subset of
 * LottieInteractivity features used across the storefront.
 */

const managedCanvases = new Set();

const ENGINE_URL = 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@0.55.0/+esm';

// Lazily import the DotLottie engine (~534 KB ESM + ~1.3 MB WASM) only when an
// animation is actually about to render. This keeps the heavyweight renderer off
// the page-load critical path, which is what froze the main thread on cold loads.
// `window.DotLottie` may already be set by a legacy inline import; reuse it if so.
const loadDotLottieEngine = (() => {
  let enginePromise;
  return () => {
    if (enginePromise) return enginePromise;

    if (window.DotLottie) {
      enginePromise = Promise.resolve(window.DotLottie);
      return enginePromise;
    }

    enginePromise = import(ENGINE_URL)
      .then((module) => {
        window.DotLottie = module.DotLottie;
        return window.DotLottie;
      })
      .catch((error) => {
        console.error('dotlottie-init: failed to load DotLottie engine', error);
        enginePromise = null; // allow a later trigger to retry
        throw error;
      });

    return enginePromise;
  };
})();

// Shared viewport gate. An animation's engine load + instantiation are deferred
// until its canvas nears the viewport. A `display:none` canvas (e.g. the hidden
// mobile/desktop variant of a responsive hero) never intersects, so it never
// loads — this also fixes the old "both responsive variants download" problem.
const lazyObserver = typeof IntersectionObserver === 'function'
  ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const canvas = entry.target;
        observer.unobserve(canvas);
        const activate = canvas.__dotActivate;
        if (typeof activate === 'function') {
          delete canvas.__dotActivate;
          activate();
        }
      });
    }, { rootMargin: '200px 0px' })
  : null;

function registerLazyCanvas(canvas, activate) {
  if (!canvas || canvas.__dotObserved) return;
  canvas.__dotObserved = true;

  if (!lazyObserver) {
    // No IntersectionObserver support: load immediately as a graceful fallback.
    activate();
    return;
  }

  canvas.__dotActivate = activate;
  lazyObserver.observe(canvas);
}

function getCanvasElement(target) {
  if (typeof target === 'string') {
    return document.querySelector(target);
  }
  return target;
}

function getCanvasMeta(canvas) {
  if (!canvas.__dotMeta) {
    canvas.__dotMeta = {
      actionCleanups: [],
      pendingActions: []
    };
    managedCanvases.add(canvas);
  }
  return canvas.__dotMeta;
}

function clearActionHandlers(meta) {
  if (!meta.actionCleanups) return;
  meta.actionCleanups.forEach((cleanup) => {
    try {
      cleanup?.();
    } catch (error) {
      console.warn('Failed to tear down DotLottie action handler', error);
    }
  });
  meta.actionCleanups = [];
}

async function createDotLottieAnimation(options) {
  const {
    canvas,
    src,
    autoplay = false,
    loop = false,
    speed = 1
  } = options;

  const canvasElement = getCanvasElement(canvas);
  if (!canvasElement) {
    console.error('dotlottie-init: canvas element not found', canvas);
    return null;
  }

  const meta = getCanvasMeta(canvasElement);

  await loadDotLottieEngine();
  if (!window.DotLottie) return null;

  if (meta.instance) {
    if (src && meta.source !== src && typeof meta.instance.load === 'function') {
      meta.instance.load(src);
      meta.source = src;
      meta.isLoaded = false;
      meta.instance.addEventListener('load', () => {
        meta.isLoaded = true;
        updateCanvasResolution(canvasElement, meta.instance);
        flushPendingActions(meta);
      }, { once: true });
    }

    if (typeof loop === 'boolean') {
      if (typeof meta.instance.setLoop === 'function') {
        meta.instance.setLoop(loop);
      } else {
        meta.instance.loop = loop;
      }
    }

    if (autoplay) {
      meta.instance.play?.();
    }

    updateCanvasResolution(canvasElement, meta.instance);
    return meta.instance;
  }

  const instance = new window.DotLottie({
    canvas: canvasElement,
    src,
    autoplay,
    loop,
    speed,
    useFrameInterpolation: true
  });

  meta.instance = instance;
  meta.source = src;
  meta.isLoaded = false;

  instance.addEventListener('load', () => {
    meta.isLoaded = true;
    updateCanvasResolution(canvasElement, instance);
    flushPendingActions(meta);
  }, { once: true });

  if (!meta.resizeCleanup) {
    meta.resizeCleanup = setupHiDPIScaling(canvasElement, instance);
  } else {
    updateCanvasResolution(canvasElement, instance);
  }

  return instance;
}

function createScrollAnimation(config) {
  const canvasElement = getCanvasElement(config.player);
  if (!canvasElement) {
    console.error('dotlottie-init: canvas element not found', config.player);
    return;
  }

  if (!canvasElement.dataset.src) {
    console.error('dotlottie-init: canvas element missing data-src attribute');
    return;
  }

  // Defer the engine load + instantiation until the canvas nears the viewport.
  registerLazyCanvas(canvasElement, () => activateScrollAnimation(config, canvasElement));
}

async function activateScrollAnimation(config, canvasElement) {
  const src = canvasElement.dataset.src;

  const actions = Array.isArray(config.actions) ? config.actions : [];
  const mode = (config.mode || 'scroll').toLowerCase();
  const loopAttr = canvasElement.dataset.loop;
  const autoplayAttr = canvasElement.dataset.autoplay;
  const hasLoopAttr = typeof loopAttr !== 'undefined';
  const wantsAutoplay = typeof autoplayAttr !== 'undefined' ? autoplayAttr !== 'false' : false;
  const loopPreference = hasLoopAttr ? loopAttr !== 'false' : undefined;

  const actionSuggestsLoop = actions.some((action) => {
    const state = typeof action?.state === 'string' ? action.state.toLowerCase() : '';
    const type = typeof action?.type === 'string' ? action.type.toLowerCase() : '';
    return state === 'loop' || type === 'loop';
  });
  const shouldLoop = actionSuggestsLoop;
  const derivedLoop = shouldLoop ? true : loopPreference;
  const shouldAutoplay =
    mode === 'play'
      ? actions.length === 0 || wantsAutoplay
      : shouldLoop || mode === 'chain';

  const instance = await createDotLottieAnimation({
    canvas: canvasElement,
    src,
    autoplay: shouldAutoplay,
    loop: derivedLoop
  });

  if (!instance) return null;

  const meta = getCanvasMeta(canvasElement);

  const applyActions = () => {
    clearActionHandlers(meta);

    if (!actions.length) {
      return;
    }

    if (mode === 'chain') {
      actions.forEach((action) => handleChainAction(instance, action));
      return;
    }

    meta.actionCleanups = actions
      .map((action) => createActionWatcher(canvasElement, instance, action))
      .filter(Boolean);
  };

  if (meta.isLoaded) {
    applyActions();
  } else {
    meta.pendingActions.push(applyActions);
  }

  return instance;
}

function handleChainAction(instance, action = {}) {
  const frames = action.frames || [0, instance.totalFrames || 0];
  const [start = 0, end] = frames;

  instance.setFrame?.(start);

  if (typeof end === 'number' && typeof instance.setSegment === 'function') {
    instance.setSegment(start, end);
  }

  const shouldLoop = action.state === 'loop';
  if (typeof instance.setLoop === 'function') {
    instance.setLoop(shouldLoop);
  } else {
    instance.loop = shouldLoop;
  }

  if (action.state === 'stop') {
    instance.stop?.();
  } else {
    instance.play?.();
  }
}

function createActionWatcher(element, instance, action = {}) {
  const type = typeof action.type === 'string' ? action.type.toLowerCase() : '';

  switch (type) {
    case 'playonce':
      return setupPlayOnce(element, instance, action);
    case 'play':
      return setupPlay(element, instance, action);
    case 'seek':
      return setupSeek(element, instance, action);
    case 'loop':
      return setupLoop(element, instance, action);
    default:
      return null;
  }
}

function setupPlayOnce(element, instance, action = {}) {
  const [startVisibility = 0, endVisibility = 1] = action.visibility || [0, 1];
  const [startFrame = 0, endFrame] = action.frames || [];
  let hasPlayed = false;

  return watchVisibility(element, (visibilityRatio) => {
    if (
      hasPlayed ||
      visibilityRatio < startVisibility ||
      visibilityRatio > endVisibility ||
      !isElementInViewport(element)
    ) {
      return false;
    }

    hasPlayed = true;
    instance.setFrame?.(startFrame);
    instance.play?.();

    if (typeof endFrame === 'number') {
      stopAtFrame(instance, endFrame);
    }

    return true;
  }, { once: true });
}

function setupPlay(element, instance, action = {}) {
  const [startVisibility = 0] = action.visibility || [0];
  const [startFrame = 0, endFrame] = action.frames || [];

  return watchVisibility(element, (visibilityRatio) => {
    if (visibilityRatio < startVisibility || !isElementInViewport(element)) {
      return false;
    }

    if (!instance.isPlaying) {
      instance.setFrame?.(startFrame);
      instance.play?.();

      if (typeof endFrame === 'number') {
        stopAtFrame(instance, endFrame);
      }
    }

    return false;
  });
}

function setupSeek(element, instance, action = {}) {
  const [startVisibility = 0, endVisibility = 1] = action.visibility || [0, 1];
  const [startFrame = 0, endFrame = startFrame] = action.frames || [0, 0];
  const frameDelta = endFrame - startFrame;

  return watchVisibility(element, (visibilityRatio) => {
    if (
      visibilityRatio < startVisibility ||
      visibilityRatio > endVisibility ||
      !isElementInViewport(element)
    ) {
      return false;
    }

    const span = endVisibility - startVisibility || 1;
    const progress = (visibilityRatio - startVisibility) / span;
    const frame = startFrame + frameDelta * progress;

    instance.setFrame?.(Math.round(frame));
    return false;
  });
}

function setupLoop(element, instance, action = {}) {
  const [startVisibility = 0, endVisibility = 1] = action.visibility || [0, 1];
  const [startFrame = 0, endFrame] = action.frames || [];
  let isLooping = false;
  const lastSegment = typeof endFrame === 'number' ? [startFrame, endFrame] : null;

  if (typeof instance.setLoop === 'function') {
    instance.setLoop(true);
  } else {
    instance.loop = true;
  }

  return watchVisibility(element, (visibilityRatio) => {
    const inRange =
      visibilityRatio >= startVisibility &&
      visibilityRatio <= endVisibility &&
      isElementInViewport(element);

    if (!inRange) {
      if (isLooping) {
        instance.pause?.();
        isLooping = false;
      }
      return false;
    }

    if (!isLooping) {
      if (lastSegment && typeof instance.setSegment === 'function') {
        instance.setSegment(lastSegment[0], lastSegment[1]);
      }
      instance.setFrame?.(startFrame);
      instance.play?.();
      isLooping = true;
    }

    return false;
  });
}

function stopAtFrame(instance, endFrame) {
  const check = () => {
    if (!instance.isPlaying) return;

    if (instance.currentFrame >= endFrame) {
      instance.pause?.();
    } else {
      requestAnimationFrame(check);
    }
  };

  requestAnimationFrame(check);
}

function watchVisibility(element, handler, options = {}) {
  const { once = false } = options;
  let stopped = false;
  let scheduled = false;

  const evaluate = () => {
    scheduled = false;
    if (stopped) return;

    const ratio = computeVisibilityRatio(element);
    const shouldStop = handler(ratio);

    if (once && shouldStop) {
      cleanup();
    }
  };

  const schedule = () => {
    if (stopped || scheduled) return;
    scheduled = true;
    requestAnimationFrame(evaluate);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  schedule();

  function cleanup() {
    if (stopped) return;
    stopped = true;
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
  }

  return cleanup;
}

function computeVisibilityRatio(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
  const totalSpan = viewportHeight + rect.height;

  if (totalSpan <= 0) return 0;

  const visible = viewportHeight - rect.top;
  const ratio = visible / totalSpan;
  return Math.min(Math.max(ratio, 0), 1);
}

function isElementInViewport(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1;

  const verticallyVisible = rect.bottom > 0 && rect.top < viewportHeight;
  const horizontallyVisible = rect.right > 0 && rect.left < viewportWidth;

  return verticallyVisible && horizontallyVisible;
}

function setupHiDPIScaling(canvas, instance) {
  const update = () => updateCanvasResolution(canvas, instance);

  update();

  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(update)
    : null;

  resizeObserver?.observe(canvas);
  window.addEventListener('resize', update);

  return () => {
    resizeObserver?.disconnect();
    window.removeEventListener('resize', update);
  };
}

function updateCanvasResolution(canvas, instance) {
  // Cap DPR at 2: Android phones report 2.5-3.5, and canvas rasterization cost
  // scales with the square of DPR. Capping cuts main-thread cost with negligible
  // visual difference on these animations.
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  const width = Math.round(rect.width * ratio) || 1;
  const height = Math.round(rect.height * ratio) || 1;

  if (canvas.width === width && canvas.height === height) {
    return;
  }

  canvas.width = width;
  canvas.height = height;
  instance?.resize?.();
}

function flushPendingActions(meta) {
  if (!meta.pendingActions?.length) return;
  const pending = [...meta.pendingActions];
  meta.pendingActions.length = 0;
  pending.forEach((fn) => {
    try {
      fn();
    } catch (error) {
      console.warn('Failed to run pending DotLottie action', error);
    }
  });
}

function initAutoplayAnimations() {
  document.querySelectorAll('canvas[data-autoplay="true"]').forEach((canvas) => {
    const src = canvas.dataset.src;
    if (!src) return;

    const loop = canvas.dataset.loop !== 'false';
    const speed = parseFloat(canvas.dataset.speed) || 1;

    // Defer instantiation (and the engine import it triggers) until the canvas
    // nears the viewport instead of firing every autoplay canvas on load.
    registerLazyCanvas(canvas, () => {
      createDotLottieAnimation({
        canvas,
        src,
        autoplay: true,
        loop,
        speed
      });
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAutoplayAnimations);
} else {
  initAutoplayAnimations();
}

window.addEventListener('beforeunload', () => {
  managedCanvases.forEach((canvas) => {
    const meta = canvas.__dotMeta;
    if (!meta) return;

    clearActionHandlers(meta);

    if (meta.resizeCleanup) {
      try {
        meta.resizeCleanup();
      } catch (error) {
        console.warn('Failed to tear down DotLottie resize handler', error);
      }
      meta.resizeCleanup = null;
    }

    meta.instance?.destroy?.();
    delete canvas.__dotMeta;
  });

  managedCanvases.clear();
});

window.createDotLottieAnimation = createDotLottieAnimation;
window.createScrollAnimation = createScrollAnimation;
window.LottieInteractivity = { create: createScrollAnimation };
